import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useRef } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import { Skeleton } from "~/components/ui/skeleton";
import {
  UnsavedChangesAlert,
  useUnsavedChangesAlert,
} from "~/components/unsaved-changes-alert";
import {
  useUnlockedKey,
  useDecryptedResource,
  useCoachShareTargets,
} from "~/lib/crypto";
import { useEncryptOnSave } from "~/lib/crypto/use-encrypt-on-save";
import {
  useOverallVision,
  useCreateOverallVision,
  useUpdateOverallVision,
} from "~/lib/queries/overallVisionQuery";
import type { OverallVisionFormData } from "~/lib/schemas/overall-vision";

import { OverallVisionForm } from "./-components/overall-vision-form";

export const Route = createFileRoute("/_user/life-design/overall-vision")({
  staticData: { title: "Overall Vision" },
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const keyManager = useUnlockedKey();
  const { data: overallVisionData } = useOverallVision();
  const createOverallVision = useCreateOverallVision();
  const updateOverallVision = useUpdateOverallVision();
  const { encryptFields } = useEncryptOnSave(keyManager);
  const coachShareTargets = useCoachShareTargets();
  const isDirtyRef = useRef(false);
  const isSubmitting =
    createOverallVision.isPending || updateOverallVision.isPending;

  const { data: decrypted, isDecrypting } = useDecryptedResource({
    resourceType: "overallVision",
    resourceId: overallVisionData?.id,
    keyManager,
    fields: { content: { encrypted: overallVisionData?.encryptedContent } },
  });

  const { showExitAlert, onDiscard, onCancel } = useUnsavedChangesAlert(
    () => isDirtyRef.current && !isSubmitting,
  );

  const handleDirtyChange = useCallback((isDirty: boolean) => {
    isDirtyRef.current = isDirty;
  }, []);

  const handleSubmit = async (data: OverallVisionFormData) => {
    if (!keyManager) {
      toast.error("Encryption is required. Please unlock your encryption key.");
      return;
    }

    try {
      const existingId = overallVisionData?.id;
      // A new vision gets its id here, so it is created once, with its keys.
      const id = existingId ?? uuidv4();
      const result = await encryptFields(
        { content: data.content },
        await coachShareTargets("vision"),
      );
      const encrypted = {
        id,
        encryptedContent: result.fields.content,
        iv: result.iv,
        keyGrants: result.keyGrants,
      };

      if (existingId) {
        await updateOverallVision.mutateAsync(encrypted);
      } else {
        await createOverallVision.mutateAsync(encrypted);
      }
      toast.success(existingId ? "Vision updated" : "Vision created");
      isDirtyRef.current = false;
      await queryClient.invalidateQueries({ queryKey: ["overall-vision"] });
      navigate({ to: "/life-design" });
    } catch (error) {
      console.error("Failed to save vision:", error);
      toast.error("Failed to save vision. Please try again.");
    }
  };

  const handleCancel = () => {
    navigate({ to: "/life-design" });
  };

  if (isDecrypting) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-[500px] w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <OverallVisionForm
        initialContent={decrypted.content ?? ""}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onDirtyChange={handleDirtyChange}
        isSubmitting={isSubmitting}
      />

      <UnsavedChangesAlert
        show={showExitAlert}
        onDiscard={onDiscard}
        onCancel={onCancel}
      />
    </div>
  );
}
